import { useEffect, useRef, useState } from 'react';
import { buildEmbed, youtubeId } from '../lib/videoEmbed';
import { Volume2, VolumeX, ExternalLink, Play } from 'lucide-react';

/**
 * KINGDM-style hero video:
 * - YouTube auto-plays muted + looping on load
 * - "Click for sound" prompt on top
 * - Click to unmute (uses YouTube IFrame API postMessage)
 * - Duration badge bottom-right
 * - 16:9 aspect, big rounded card
 * - Falls back to click-to-play poster for non-YouTube embeds
 */
export default function HeroVideo({ platform, url, duration }) {
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [interacted, setInteracted] = useState(false);
  const [fallbackPlay, setFallbackPlay] = useState(false);
  const embed = buildEmbed(platform, url);
  const ytId = platform === 'youtube' ? youtubeId(url) : null;

  // For YouTube: auto-play muted + loop. We use the privacy-enhanced domain.
  // mute=1 + autoplay=1 is allowed by browsers.
  // playlist=ID + loop=1 makes it loop.
  // enablejsapi=1 lets us postMessage to control mute state.
  const ytSrc = ytId
    ? `https://www.youtube-nocookie.com/embed/${ytId}` +
      `?autoplay=1&mute=1&loop=1&playlist=${ytId}` +
      `&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1` +
      `&iv_load_policy=3&disablekb=1`
    : null;

  // Toggle mute via YouTube IFrame API
  function toggleSound() {
    if (!iframeRef.current) return;
    const cmd = muted
      ? { event: 'command', func: 'unMute', args: [] }
      : { event: 'command', func: 'mute', args: [] };
    try {
      iframeRef.current.contentWindow.postMessage(JSON.stringify(cmd), '*');
      // Also reset volume so it isn't 0
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'setVolume', args: [80] }),
        '*'
      );
    } catch (e) { /* noop */ }
    setMuted((m) => !m);
    setInteracted(true);
  }

  // When iframe loads, send a "listening" handshake to enable command mode
  useEffect(() => {
    if (!iframeRef.current || !ytId) return;
    const t = setTimeout(() => {
      try {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: 'listening' }),
          '*'
        );
      } catch (e) { /* noop */ }
    }, 800);
    return () => clearTimeout(t);
  }, [ytId]);

  // ---------- YOUTUBE: KINGDM-style auto-playing video ----------
  if (ytId) {
    return (
      <div className="relative" data-testid="hero-video-embed">
        <div className="relative rounded-3xl overflow-hidden bg-black gold-hairline shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
          <div className="relative aspect-video">
            <iframe
              ref={iframeRef}
              title="AlveeTrade hero video"
              src={ytSrc}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />

            {/* Subtle bottom gradient for badge legibility */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            {/* "Click for sound" overlay — only visible while muted */}
            {muted && (
              <button
                type="button"
                onClick={toggleSound}
                aria-label="Click to unmute"
                data-testid="hero-video-unmute"
                className="absolute inset-0 flex items-center justify-center group cursor-pointer"
              >
                <span className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
                <span className="relative flex flex-col items-center gap-3">
                  <span className="relative flex items-center justify-center h-16 w-16 rounded-full bg-[hsl(0_84%_52%)]/95 shadow-[0_10px_40px_rgba(225,29,46,0.55)] group-hover:scale-105 transition-transform">
                    <VolumeX className="h-7 w-7 text-white" />
                    <span className="absolute inset-0 rounded-full animate-ping opacity-30 bg-[hsl(0_84%_52%)]" />
                  </span>
                  <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[11px] uppercase tracking-[0.18em] text-white font-medium">
                    Click for sound
                  </span>
                </span>
              </button>
            )}

            {/* Sound toggle pill (visible after first interaction) */}
            {!muted && (
              <button
                type="button"
                onClick={toggleSound}
                aria-label="Mute"
                data-testid="hero-video-mute"
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-[11px] hover:bg-black/75 transition-colors"
              >
                <Volume2 className="h-3.5 w-3.5" /> ON
              </button>
            )}

            {/* Brand pill bottom-left */}
            <div className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] font-medium">
              AlveeTrade · est. AlveeFX
            </div>

            {/* Duration badge bottom-right (KINGDM-style) */}
            {duration && (
              <div className="absolute bottom-3 right-3 inline-flex items-center px-2 py-1 rounded-md bg-black/70 text-white text-xs font-mono tabular-nums">
                {duration}
              </div>
            )}
          </div>
        </div>

        {/* Tiny helper text below */}
        <div className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {interacted ? 'Watch in full · 60-second pitch' : 'Tap video to hear the full pitch'}
        </div>
      </div>
    );
  }

  // ---------- NON-YOUTUBE FALLBACK (Facebook / Telegram / MP4) ----------
  return (
    <div className="relative" data-testid="hero-video-embed">
      <div className="relative rounded-3xl overflow-hidden bg-black gold-hairline shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
        <div className="relative aspect-video">
          {!fallbackPlay ? (
            <button
              type="button"
              onClick={() => {
                if (embed.kind === 'link') {
                  window.open(embed.fallback || url, '_blank', 'noopener');
                } else {
                  setFallbackPlay(true);
                }
              }}
              className="absolute inset-0 group"
              aria-label="Play hero video"
              data-testid="hero-video-play-button"
            >
              {embed.thumbnail && (
                <img src={embed.thumbnail} alt="Hero video" className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'radial-gradient(60% 60% at 50% 45%, rgba(245,185,66,0.22), transparent 70%), linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.85))',
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex items-center justify-center h-20 w-20 rounded-full cta-sheen border-0 group-hover:scale-105 transition-transform shadow-[0_10px_40px_rgba(225,29,46,0.55)]">
                  <Play className="h-8 w-8 text-white" fill="currentColor" />
                  <span className="absolute inset-0 rounded-full animate-ping opacity-30 bg-[hsl(0_84%_52%)]" />
                </span>
              </div>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)]">
                  AlveeTrade · est. AlveeFX
                </div>
                {duration ? (
                  <span className="text-[10px] text-white/80 font-mono tabular-nums px-2 py-0.5 bg-black/60 rounded">
                    {duration}
                  </span>
                ) : (
                  <span className="text-[10px] text-white/70 inline-flex items-center gap-1">Watch Now</span>
                )}
              </div>
            </button>
          ) : embed.kind === 'video' ? (
            <video src={embed.src} controls autoPlay className="absolute inset-0 w-full h-full object-cover" />
          ) : embed.kind === 'iframe' || embed.kind === 'telegram' ? (
            <iframe
              title="Hero video"
              src={embed.src + (embed.src.includes('?') ? '&autoplay=1' : '?autoplay=1')}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <a
              href={embed.fallback || url}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0 flex items-center justify-center text-white/80 hover:text-white gap-2"
            >
              <ExternalLink className="h-4 w-4" /> Open video
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
